import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    LinearProgress,
    Pagination,
    Switch,
    TextField,
    Typography
} from '@mui/material';
import { IconComponent } from '@zippeditoolsjs/zippedi-icons';
import { format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useAchievementColors } from '../hooks/useAchievementColors';
import BulkAchievementAssignmentModal from './BulkAchievementAssignmentModal';
import BulkUserAchievementAssignmentModal from './BulkUserAchievementAssignmentModal';

// Dummy data for users and achievements
const dummyUsers = [
    {
        avatar: "https://lh3.googleusercontent.com/a/ACg8ocJpMZLiA8PeULTonE2D_A_TpcGZenDSKsQsBjljqp38f4g1yw=s96-c",
        can_create_projects: false,
        email: "alvaro.romero-int@zippedi.com",
        first_name: "Romero",
        is_admin: false,
        label: "Álvaro",
        last_name: "Álvaro",
        value: 205
    }
];

const dummyAchievements = [
    { id: 1, name: 'First Commit', achievement_type: 'easy' },
    { id: 2, name: 'Bug Hunter', achievement_type: 'medium' },
    { id: 3, name: 'Legendary Merge', achievement_type: 'legendary' },
    { id: 4, name: 'Code Reviewer', achievement_type: 'hard' },
    { id: 5, name: 'Team Player', achievement_type: 'easy' },
];

// Dummy achievement progress data based on the table structure
const dummyAchievementProgress = [
    {
        achievement_progress_id: 1,
        achievement_id: 1,
        user_id: 1,
        is_completed: true,
        action_required_count: 5,
        created_at: '2024-01-15T10:00:00Z',
        updated_at: '2024-01-20T15:30:00Z',
        completed_at: '2024-01-20T15:30:00Z',
        is_seen: true,
        achievement_name: 'First Commit',
        achievement_type: 'easy',
        achievement_description: 'First commit to the repository',
        comment: 'Initial commit for the project setup.'
    },
];

const AchievementAssignment = (props) => {
    const {
        users = dummyUsers,
        isUsersLoading = false,
        getUsers,
        allAchievements = dummyAchievements,
        isAllAchievementsLoading = false,
        getAllAchievements,
        achievementsProgress = dummyAchievementProgress,
        isAchievementsProgressLoading,
        getAchievementsProgress,
        postAchievementsProgress,
        patchAchievementsProgress,
        achievementProgressPostingMessage,
        isAchievementsProgressPosting,
        achievementProgressDeletingMessage,
        isAchievementsProgressDeleting,
        deleteAchievementsProgress,
        achievementsTypes = [],
        getAchievementsTypes,
        achievementProgressPatchingMessage,
        postAchievementsProgressBulk,
        isPostingBulk,
        teams,
        isTeamsLoading,
        getTeams,
    } = props;

    // Use dynamic achievement colors
    const getAchievementColors = useAchievementColors(achievementsTypes);

    const [achievementProgress, setAchievementProgress] = useState(achievementsProgress || []);
    const [expandedUsers, setExpandedUsers] = useState(new Set());
    const [openDialog, setOpenDialog] = useState(false);
    const [openBulkDialog, setOpenBulkDialog] = useState(false);
    const [openBulkUserDialog, setOpenBulkUserDialog] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({
        achievement: null,
        action_required_count: 1,
        is_completed: false,
        comment: '',
        completed_at: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm')
    });
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [achievementSearchTerms, setAchievementSearchTerms] = useState({});
    const [pendingAchievement, setPendingAchievement] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [achievementToDelete, setAchievementToDelete] = useState(null);
    const [deletingAchievementId, setDeletingAchievementId] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editingProgress, setEditingProgress] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [usersPerPage, setUsersPerPage] = useState(6); // 2x3 grid layout
    const [isSaving, setIsSaving] = useState(false);
    const [pendingPatchUpdates, setPendingPatchUpdates] = useState([]);

    useEffect(() => {
        if (teams.length === 0 && !isTeamsLoading) {
            getTeams();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!isUsersLoading && !users.length) {
            getUsers();
        }
        if (!isAchievementsProgressLoading) {
            getAchievementsProgress();
        }
        if (!isAllAchievementsLoading) {
            getAllAchievements();
        }
        if (!achievementsTypes.length) {
            getAchievementsTypes();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getUsers, getAchievementsProgress, getAllAchievements, getAchievementsTypes]);

    // Update local state when achievementsProgress changes
    useEffect(() => {
        if (achievementsProgress) {
            setAchievementProgress(achievementsProgress);
        }
    }, [achievementsProgress]);

    // Handle successful achievement progress posting
    useEffect(() => {
        if (achievementProgressPostingMessage &&
            achievementProgressPostingMessage === 'Achievement progress inserted successfully' &&
            pendingAchievement) {
            // Add the pending achievement to local state
            setAchievementProgress(prev => [...prev, pendingAchievement]);
            // Clear the pending achievement
            setPendingAchievement(null);
        }
    }, [achievementProgressPostingMessage, pendingAchievement]);

    // Handle successful achievement progress deletion
    useEffect(() => {
        if (achievementProgressDeletingMessage &&
            achievementProgressDeletingMessage === 'Achievement progress deleted successfully' &&
            deletingAchievementId) {
            // Remove the deleted achievement from local state
            setAchievementProgress(prev => prev.filter(progress => progress.achievement_progress_id !== deletingAchievementId));
            // Clear the deleting achievement ID
            setDeletingAchievementId(null);
        }
    }, [achievementProgressDeletingMessage, deletingAchievementId]);

    const handleUserToggle = (userId) => {
        const newExpanded = new Set(expandedUsers);
        if (newExpanded.has(userId)) {
            newExpanded.delete(userId);
        } else {
            newExpanded.add(userId);
        }
        setExpandedUsers(newExpanded);
    };

    const handleOpenDialog = (user) => {
        setSelectedUser(user);
        setFormData({
            achievement: null,
            action_required_count: 1,
            is_completed: false,
            comment: '',
            completed_at: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm')
        });
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedUser(null);
        setFormData({
            achievement: null,
            action_required_count: 1,
            is_completed: false,
            comment: '',
            completed_at: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm')
        });
    };

    const handleSubmit = () => {
        if (formData.achievement && selectedUser) {
            const now = format(new Date(), 'yyyy-MM-dd\'T\'HH:mm:ss');
            const completedAt = formData.is_completed && formData.completed_at
                ? format(new Date(formData.completed_at), 'yyyy-MM-dd\'T\'HH:mm:ss')
                : null;
            const newProgress = {
                achievement_progress_id: Math.max(...achievementProgress.map(p => p.achievement_progress_id), 0) + 1,
                achievement_id: formData.achievement.achievement_id,
                user_id: selectedUser.value,
                is_completed: formData.is_completed,
                action_required_count: formData.action_required_count,
                created_at: now,
                completed_at: completedAt,
                is_seen: false,
                updated_at: now,
                achievement_name: formData.achievement.name,
                achievement_type: formData.achievement.achievement_type,
                achievement_description: formData.achievement.description || '',
                comment: formData.comment || ''
            };
            setPendingAchievement(newProgress);
            postAchievementsProgress(newProgress);
            handleCloseDialog();
        }
    };



    const getUserAchievements = (userId) => {
        return achievementProgress.filter(progress => progress.user_id === userId);
    };

    const getUserAchievementCount = (userId) => {
        return getUserAchievements(userId).length;
    };

    const filteredUsers = users.filter(user =>
        `${user.first_name} ${user.last_name}`.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(userSearchTerm.toLowerCase())
    );

    // Pagination logic
    const indexOfLastUser = currentPage * usersPerPage;
    const indexOfFirstUser = indexOfLastUser - usersPerPage;
    const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

    // Reset to first page when search term changes
    useEffect(() => {
        setCurrentPage(1);
    }, [userSearchTerm]);

    const handlePageChange = (event, page) => {
        setCurrentPage(page);
    };



    const getFilteredUserAchievements = (userId) => {
        const userAchievements = getUserAchievements(userId);
        const searchTerm = achievementSearchTerms[userId] || '';

        if (!searchTerm) return userAchievements;

        return userAchievements.filter(progress =>
            progress.achievement_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            progress.achievement_type.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const handleAchievementSearchChange = (userId, value) => {
        setAchievementSearchTerms(prev => ({
            ...prev,
            [userId]: value
        }));
    };

    const handleDeleteClick = (progress) => {
        setAchievementToDelete(progress);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (achievementToDelete) {
            setDeletingAchievementId(achievementToDelete.achievement_progress_id);
            deleteAchievementsProgress(achievementToDelete.achievement_progress_id);
            setDeleteDialogOpen(false);
            setAchievementToDelete(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setAchievementToDelete(null);
    };

    const handleEditModeToggle = () => {
        if (editMode) {
            // Cancel edit mode - discard changes
            setEditMode(false);
            setEditingProgress({});
            setHasChanges(false);
        } else {
            // Enter edit mode
            setEditMode(true);
        }
    };

    const handleEditProgress = (progressId, field, value) => {
        setEditingProgress(prev => ({
            ...prev,
            [progressId]: {
                ...prev[progressId],
                [field]: value
            }
        }));
        setHasChanges(true);
    };

    const handleSaveChanges = () => {
        const updates = Object.entries(editingProgress).map(([progressId, changes]) => ({
            achievement_progress_id: parseInt(progressId),
            ...changes,
            updated_at: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm:ss')
        }));
        setPendingPatchUpdates(updates);
        setIsSaving(true);
        updates.forEach(update => patchAchievementsProgress(update));
    };

    useEffect(() => {
        if (
            isSaving &&
            achievementProgressPatchingMessage === 'Achievement progress updated successfully'
        ) {
            setAchievementProgress(prev => prev.map(progress => {
                const update = editingProgress[progress.achievement_progress_id];
                if (update) {
                    return {
                        ...progress,
                        ...update,
                        updated_at: format(new Date(), 'yyyy-MM-dd\'T\'HH:mm:ss')
                    };
                }
                return progress;
            }));
            setEditingProgress({});
            setHasChanges(false);
            setIsSaving(false);
            setPendingPatchUpdates([]);
        }
    }, [achievementProgressPatchingMessage, isSaving]);

    const filteredAndSortedAchievements = useMemo(() => {
        if (!selectedUser) return [];

        const userAchievementIds = new Set(
            achievementProgress
                .filter(p => p.user_id === selectedUser.value)
                .map(p => p.achievement_id)
        );

        return allAchievements
            .filter(a => !userAchievementIds.has(a.achievement_id))
            .sort((a, b) => {
                const typeCompare = (a.achievement_type || '').localeCompare(b.achievement_type || '');
                if (typeCompare !== 0) return typeCompare;
                return (a.name || '').localeCompare(b.name || '');
            });
    }, [allAchievements, achievementProgress, selectedUser]);

    return (
        <Box>
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Assign Achievements
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="contained"
                        startIcon={<IconComponent iconName="people" />}
                        onClick={() => setOpenBulkDialog(true)}
                    >
                        <Typography variant="button" sx={{ textTransform: 'none', fontWeight: 600 }}>
                            Add User Bulk
                        </Typography>
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<IconComponent iconName="trophy" />}
                        onClick={() => setOpenBulkUserDialog(true)}
                        sx={{
                            background: 'linear-gradient(130deg, #1a242d 0%, #0f82b7 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0b1014 0%, #0b597d 100%)',
                            }
                        }}
                    >
                        <Typography variant="button" sx={{ textTransform: 'none', fontWeight: 600 }}>
                            Add Achievements Bulk
                        </Typography>
                    </Button>
                </Box>
            </Box>

            {/* User Search */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search users by name, team, or email..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <IconComponent iconName="search" style={{ marginRight: 8, color: 'text.secondary' }} />
                        ),
                        endAdornment: userSearchTerm && (
                            <IconButton
                                size="small"
                                onClick={() => setUserSearchTerm('')}
                                sx={{ marginRight: -8 }}
                            >
                                <IconComponent iconName="x" style={{ fontSize: 16 }} />
                            </IconButton>
                        )
                    }}
                    size="small"
                />
                {userSearchTerm && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                        Showing {filteredUsers.length} of {users.length} users
                    </Typography>
                )}
                {!userSearchTerm && users.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                        Showing {currentUsers.length} of {users.length} users (Page {currentPage} of {totalPages})
                    </Typography>
                )}
            </Box>

            {/* Users List */}
            <Grid container spacing={2}>
                {currentUsers.map((user) => {
                    const userAchievements = getUserAchievements(user.value);
                    const isExpanded = expandedUsers.has(user.value);

                    return (
                        <Grid item xs={12} key={user.value}>
                            <Card sx={{
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: 3,
                                    transform: 'translateY(-2px)'
                                }
                            }}>
                                <CardContent sx={{ pt: 1, px: 1.5, pb: 0, '&:last-child': { py: 1 } }}>
                                    {/* User Info Row */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            mb: isExpanded ? 2 : 0,
                                            py: 0
                                        }}
                                        onClick={() => handleUserToggle(user.value)}
                                    >
                                        <Avatar
                                            sx={{
                                                width: 24,
                                                height: 24,
                                                bgcolor: '#0070B7',
                                                fontSize: '0.75rem'
                                            }}
                                            src={user.avatar}
                                        >
                                            {`${user.first_name} ${user.last_name}`.split(' ').map(n => n[0]).join('')}
                                        </Avatar>

                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                                                {user.first_name} {user.last_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.1, fontSize: '0.7rem' }}>
                                                {user.email}
                                            </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Chip
                                                label={`${getUserAchievementCount(user.value)} achievements`}
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ fontSize: '0.65rem', height: 20 }}
                                            />
                                            {isAchievementsProgressPosting && pendingAchievement && pendingAchievement.user_id === user.value && (
                                                <IconComponent
                                                    iconName="spinner"
                                                    style={{ fontSize: 16, color: '#0070B7', animation: 'spin 1s linear infinite' }}
                                                />
                                            )}
                                            {isAchievementsProgressDeleting && deletingAchievementId &&
                                                getUserAchievements(user.value).some(p => p.achievement_progress_id === deletingAchievementId) && (
                                                    <IconComponent
                                                        iconName="spinner"
                                                        style={{ fontSize: 16, color: '#d32f2f', animation: 'spin 1s linear infinite' }}
                                                    />
                                                )}
                                            <IconButton size="small" sx={{ p: 0.25 }}>
                                                <IconComponent
                                                    iconName={isExpanded ? "chevron-up" : "chevron-down"}
                                                    style={{ fontSize: 16 }}
                                                />
                                            </IconButton>
                                        </Box>
                                    </Box>

                                    {/* Expandable Achievements Section */}
                                    <Collapse in={isExpanded}>
                                        <Divider sx={{ my: 2 }} />

                                        {/* Loading indicator for pending achievement */}
                                        {isAchievementsProgressPosting && pendingAchievement && pendingAchievement.user_id === user.value && (
                                            <Box sx={{ mb: 2 }}>
                                                <LinearProgress
                                                    sx={{
                                                        height: 4,
                                                        borderRadius: 2,
                                                        backgroundColor: 'rgba(0, 112, 183, 0.1)',
                                                        '& .MuiLinearProgress-bar': {
                                                            backgroundColor: '#0070B7'
                                                        }
                                                    }}
                                                />
                                                <Typography variant="caption" color="primary" sx={{ mt: 0.5, display: 'block' }}>
                                                    Adding achievement...
                                                </Typography>
                                            </Box>
                                        )}

                                        {/* Loading indicator for deleting achievement */}
                                        {isAchievementsProgressDeleting && deletingAchievementId &&
                                            getUserAchievements(user.value).some(p => p.achievement_progress_id === deletingAchievementId) && (
                                                <Box sx={{ mb: 2 }}>
                                                    <LinearProgress
                                                        sx={{
                                                            height: 4,
                                                            borderRadius: 2,
                                                            backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                                            '& .MuiLinearProgress-bar': {
                                                                backgroundColor: '#d32f2f'
                                                            }
                                                        }}
                                                    />
                                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                                        Deleting achievement...
                                                    </Typography>
                                                </Box>
                                            )}

                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                                Achievements
                                            </Typography>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button
                                                    variant={editMode ? "outlined" : "outlined"}
                                                    size="small"
                                                    startIcon={<IconComponent iconName={editMode ? "x" : "pencil"} />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleEditModeToggle();
                                                    }}
                                                    disabled={isAchievementsProgressPosting || isAchievementsProgressDeleting}
                                                    color={editMode ? "error" : "primary"}
                                                    sx={{
                                                        ...(editMode && {
                                                            borderColor: '#d32f2f',
                                                            color: '#d32f2f',
                                                            '&:hover': {
                                                                borderColor: '#b71c1c',
                                                                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                                                            },
                                                        }),
                                                        '&:disabled': {
                                                            background: 'rgba(0, 0, 0, 0.12)',
                                                            color: 'rgba(0, 0, 0, 0.38)'
                                                        }
                                                    }}
                                                >
                                                    {editMode ? 'Cancel' : 'Edit Mode'}
                                                </Button>
                                                {editMode && hasChanges && (
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        startIcon={<IconComponent iconName="check" />}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSaveChanges();
                                                            setEditMode(false);
                                                        }}
                                                        disabled={isAchievementsProgressPosting || isAchievementsProgressDeleting}
                                                        color="success"
                                                        sx={{
                                                            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                                            '&:hover': {
                                                                background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                                                            },
                                                            '&:disabled': {
                                                                background: 'rgba(0, 0, 0, 0.12)',
                                                                color: 'rgba(0, 0, 0, 0.38)'
                                                            }
                                                        }}
                                                    >
                                                        Save Changes
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="contained"
                                                    size="small"
                                                    startIcon={<IconComponent iconName="plus" />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleOpenDialog(user);
                                                    }}
                                                    disabled={isAchievementsProgressPosting}
                                                    sx={{
                                                        background: 'linear-gradient(135deg, #1a242d 0%, #0f82b7 100%)',
                                                        '&:hover': {
                                                            background: 'linear-gradient(135deg, #1a242d 0%, #0f82b7 100%)',
                                                        },
                                                        '&:disabled': {
                                                            background: 'rgba(0, 0, 0, 0.12)',
                                                            color: 'rgba(0, 0, 0, 0.38)'
                                                        }
                                                    }}
                                                >
                                                    Add Achievement
                                                </Button>
                                            </Box>
                                        </Box>

                                        {/* Achievement Search */}
                                        <Box sx={{ mb: 2 }}>
                                            <TextField
                                                fullWidth
                                                placeholder="Search achievements by name or type..."
                                                value={achievementSearchTerms[user.value] || ''}
                                                onChange={(e) => handleAchievementSearchChange(user.value, e.target.value)}
                                                InputProps={{
                                                    startAdornment: (
                                                        <IconComponent iconName="search" style={{ marginRight: 8, color: 'text.secondary' }} />
                                                    ),
                                                    endAdornment: achievementSearchTerms[user.value] && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleAchievementSearchChange(user.value, '')}
                                                            sx={{ marginRight: -8 }}
                                                        >
                                                            <IconComponent iconName="x" style={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )
                                                }}
                                                size="small"
                                            />
                                        </Box>

                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            {getFilteredUserAchievements(user.value).map((progress) => {
                                                const difficultyColors = getAchievementColors(progress.achievement_type);
                                                return (
                                                    <Box
                                                        key={progress.achievement_progress_id}
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 2,
                                                            p: 2,
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: 1,
                                                            bgcolor: 'background.paper',
                                                            '&:hover': {
                                                                bgcolor: 'action.hover'
                                                            }
                                                        }}
                                                    >
                                                        {/* Trophy Icon and Achievement Name */}
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 200 }}>
                                                            <IconComponent
                                                                iconName="trophy"
                                                                style={{ fontSize: 16, color: '#fbbf24' }}
                                                            />
                                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                                {progress.achievement_name}
                                                            </Typography>
                                                        </Box>

                                                        {/* Difficulty Chip */}
                                                        <Chip
                                                            label={progress.achievement_type}
                                                            size="small"
                                                            sx={{
                                                                ...difficultyColors,
                                                                fontSize: '10px',
                                                                fontWeight: 600,
                                                                minWidth: 80
                                                            }}
                                                        />

                                                        {/* Action Required Count */}
                                                        <TextField
                                                            label="Count"
                                                            type="number"
                                                            value={editMode && editingProgress[progress.achievement_progress_id]?.action_required_count !== undefined
                                                                ? editingProgress[progress.achievement_progress_id].action_required_count
                                                                : progress.action_required_count}
                                                            onChange={(e) => {
                                                                if (editMode) {
                                                                    handleEditProgress(
                                                                        progress.achievement_progress_id,
                                                                        'action_required_count',
                                                                        parseInt(e.target.value) || 0
                                                                    );
                                                                }
                                                            }}
                                                            size="small"
                                                            inputProps={{ min: 0 }}
                                                            sx={{
                                                                width: 100,
                                                                ...(editMode && editingProgress[progress.achievement_progress_id]?.action_required_count !== undefined && {
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderColor: '#f59e0b',
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: '#d97706',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: '#f59e0b',
                                                                        }
                                                                    }
                                                                })
                                                            }}
                                                            disabled={!editMode || isAchievementsProgressPosting || isAchievementsProgressDeleting}
                                                        />

                                                        {/* Comment */}
                                                        <TextField
                                                            label="Comment"
                                                            multiline
                                                            rows={1}
                                                            value={editMode && editingProgress[progress.achievement_progress_id]?.comment !== undefined
                                                                ? editingProgress[progress.achievement_progress_id].comment
                                                                : progress.comment}
                                                            onChange={(e) => {
                                                                if (editMode) {
                                                                    handleEditProgress(
                                                                        progress.achievement_progress_id,
                                                                        'comment',
                                                                        e.target.value
                                                                    );
                                                                }
                                                            }}
                                                            size="small"
                                                            sx={{
                                                                width: 200,
                                                                ...(editMode && editingProgress[progress.achievement_progress_id]?.comment !== undefined && {
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderColor: '#f59e0b',
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: '#d97706',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: '#f59e0b',
                                                                        }
                                                                    }
                                                                })
                                                            }}
                                                            disabled={!editMode || isAchievementsProgressPosting || isAchievementsProgressDeleting}
                                                        />

                                                        {/* Completed Switch */}
                                                        <FormControlLabel
                                                            control={
                                                                <Switch
                                                                    checked={editMode && editingProgress[progress.achievement_progress_id]?.is_completed !== undefined
                                                                        ? editingProgress[progress.achievement_progress_id].is_completed
                                                                        : progress.is_completed}
                                                                    onChange={(e) => {
                                                                        if (editMode) {
                                                                            handleEditProgress(
                                                                                progress.achievement_progress_id,
                                                                                'is_completed',
                                                                                e.target.checked
                                                                            );
                                                                        }
                                                                    }}
                                                                    size="small"
                                                                    disabled={!editMode || isAchievementsProgressPosting || isAchievementsProgressDeleting}
                                                                    sx={{
                                                                        ...(editMode && editingProgress[progress.achievement_progress_id]?.is_completed !== undefined && {
                                                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                                                color: '#f59e0b',
                                                                            },
                                                                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                                                backgroundColor: '#f59e0b',
                                                                            }
                                                                        })
                                                                    }}
                                                                />
                                                            }
                                                            label={
                                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                    <span>Completed</span>
                                                                    {editMode && editingProgress[progress.achievement_progress_id]?.is_completed !== undefined && (
                                                                        <IconComponent
                                                                            iconName="pencil"
                                                                            style={{ fontSize: 12, color: '#f59e0b' }}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            }
                                                            sx={{
                                                                '& .MuiFormControlLabel-label': {
                                                                    fontSize: '0.75rem'
                                                                },
                                                                minWidth: 120
                                                            }}
                                                        />

                                                        {/* Created Date */}
                                                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 100 }}>
                                                            {new Date(progress.created_at).toLocaleDateString()}
                                                        </Typography>

                                                        {/* Completed Date (if completed) */}
                                                        {progress.is_completed && progress.completed_at && (
                                                            <Typography variant="caption" color="success.main" sx={{ minWidth: 120 }}>
                                                                ✓ {new Date(progress.completed_at).toLocaleDateString()}
                                                            </Typography>
                                                        )}

                                                        {/* Comment (if exists) */}
                                                        {progress.comment && (
                                                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 150, fontStyle: 'italic' }}>
                                                                "{progress.comment}"
                                                            </Typography>
                                                        )}

                                                        {/* Delete Button */}
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDeleteClick(progress)}
                                                            disabled={isAchievementsProgressDeleting}
                                                            sx={{
                                                                color: 'error.main',
                                                                '&:hover': {
                                                                    backgroundColor: 'error.light',
                                                                    color: 'error.contrastText'
                                                                }
                                                            }}
                                                        >
                                                            {isAchievementsProgressDeleting && deletingAchievementId === progress.achievement_progress_id ? (
                                                                <IconComponent iconName="spinner" style={{ fontSize: 16, animation: 'spin 1s linear infinite' }} />
                                                            ) : (
                                                                <IconComponent iconName="trash" style={{ fontSize: 16 }} />
                                                            )}
                                                        </IconButton>
                                                    </Box>
                                                );
                                            })}

                                            {getFilteredUserAchievements(user.value).length === 0 && (
                                                <Box sx={{
                                                    textAlign: 'center',
                                                    py: 4,
                                                    color: 'text.secondary'
                                                }}>
                                                    <IconComponent iconName="trophy" style={{ fontSize: 48, opacity: 0.3 }} />
                                                    <Typography variant="body1" sx={{ mt: 1 }}>
                                                        {userAchievements.length === 0
                                                            ? 'No achievements assigned yet'
                                                            : 'No achievements match your search'
                                                        }
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Collapse>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="medium"
                        showFirstButton
                        showLastButton
                        sx={{
                            '& .MuiPaginationItem-root': {
                                borderRadius: '8px',
                                margin: '0 4px',
                            },
                            '& .Mui-selected': {
                                background: 'linear-gradient(135deg, #1a242d 0%, #0f82b7 100%)',
                                color: 'white',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #0b1014 0%, #0b597d 100%)',
                                }
                            }
                        }}
                    />
                </Box>
            )}

            {/* No users message */}
            {filteredUsers.length === 0 && !userSearchTerm && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <IconComponent iconName="users" style={{ fontSize: 48, color: 'gray', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                        No users found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        No users are available for achievement assignment
                    </Typography>
                </Box>
            )}

            {/* Add Achievement Dialog */}
            <BulkAchievementAssignmentModal
                open={openBulkDialog}
                onClose={() => setOpenBulkDialog(false)}
                users={users}
                allAchievements={allAchievements}
                achievementsProgress={achievementProgress}
                postAchievementsProgressBulk={postAchievementsProgressBulk}
                isPostingBulk={isPostingBulk}
                getAchievementColors={getAchievementColors}
                teams={teams}
            />
            <BulkUserAchievementAssignmentModal
                open={openBulkUserDialog}
                onClose={() => setOpenBulkUserDialog(false)}
                users={users}
                allAchievements={allAchievements}
                achievementsProgress={achievementProgress}
                postAchievementsProgressBulk={postAchievementsProgressBulk}
                isPostingBulk={isPostingBulk}
                teams={teams}
            />
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add Achievement to {selectedUser ? `${selectedUser.first_name} ${selectedUser.last_name}` : ''}</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                options={filteredAndSortedAchievements}
                                getOptionLabel={(option) => option.name}
                                value={formData.achievement}
                                onChange={(event, newValue) => setFormData(prev => ({ ...prev, achievement: newValue }))}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select Achievement"
                                        required
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Chip
                                                label={option.achievement_type}
                                                size="small"
                                                sx={{
                                                    ...getAchievementColors(option.achievement_type),
                                                    fontSize: '10px',
                                                    fontWeight: 600
                                                }}
                                            />
                                            <Typography>{option.name}</Typography>
                                        </Box>
                                    </Box>
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Action Required Count"
                                type="number"
                                value={formData.action_required_count}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    action_required_count: parseInt(e.target.value) || 1
                                }))}
                                inputProps={{ min: 1 }}
                                helperText="Number of actions required to complete this achievement"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.is_completed}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            is_completed: e.target.checked
                                        }))}
                                    />
                                }
                                label="Mark as completed"
                                sx={{
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '0.875rem'
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Completion date"
                                type="datetime-local"
                                value={formData.completed_at}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    completed_at: e.target.value
                                }))}
                                disabled={!formData.is_completed}
                                required={formData.is_completed}
                                InputLabelProps={{ shrink: true }}
                                helperText={formData.is_completed
                                    ? 'Date this achievement was completed'
                                    : 'Enable "Mark as completed" to set a completion date'}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Comment"
                                multiline
                                rows={3}
                                value={formData.comment}
                                onChange={(e) => setFormData(prev => ({
                                    ...prev,
                                    comment: e.target.value
                                }))}
                                helperText="Add a comment for this achievement assignment"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} disabled={isAchievementsProgressPosting}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={!formData.achievement || isAchievementsProgressPosting || (formData.is_completed && !formData.completed_at)}
                        startIcon={isAchievementsProgressPosting ? <IconComponent iconName="spinner" /> : null}
                    >
                        {isAchievementsProgressPosting ? 'Adding...' : 'Add Achievement'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconComponent iconName="trash" style={{ color: '#d32f2f', fontSize: 20 }} />
                    Confirm Delete
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Are you sure you want to delete this achievement progress?
                    </Typography>
                    {achievementToDelete && (
                        <Box sx={{
                            p: 2,
                            bgcolor: 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'grey.300'
                        }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                {achievementToDelete.achievement_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Type: {achievementToDelete.achievement_type}
                            </Typography>
                            {achievementToDelete.action_required_count && (
                                <Typography variant="body2" color="text.secondary">
                                    Actions Required: {achievementToDelete.action_required_count} (Group ID #{achievementToDelete.group_id})
                                </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                                Status: {achievementToDelete.is_completed ? 'Completed' : 'In Progress'}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDeleteCancel} disabled={isAchievementsProgressDeleting}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        disabled={isAchievementsProgressDeleting}
                        startIcon={isAchievementsProgressDeleting ? <IconComponent iconName="spinner" /> : <IconComponent iconName="trash" />}
                    >
                        {isAchievementsProgressDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AchievementAssignment;
